import { CommonModule } from '@angular/common';
import {
  Component,
  effect,
  HostListener,
  inject,
  input,
  OnChanges,
  OnDestroy,
  output,
  signal,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { DictionaryService } from '../../services/dictionary.service';
import { SnackbarService } from '../../services/snackbar.service';
import { AuthenticationService } from '../../services/authentication.service';
import { ModalComponent } from '../../components/modal/modal.component';
import { TicketConversationStore } from './application/ticket-conversation.store';
import { TicketApiService } from './data-access/ticket-api.service';
import { TicketConversationScrollController } from './domain/ticket-conversation-scroll';
import { ticketApiErrorMessage } from './domain/ticket-errors';
import { ticketAttachmentKind } from './domain/ticket-attachment-types';
import { TicketFileSelectionError } from './domain/ticket-file-selection';
import {
  TicketReplyPayload,
  TicketSenderRole,
  TicketStatus,
} from './domain/ticket.models';
import {
  TicketAttachmentPreview,
  TicketAttachmentPreviewComponent,
} from './ui/attachment-preview/ticket-attachment-preview.component';
import {
  TicketAttachmentOpenEvent,
  TicketMessageListComponent,
} from './ui/message-list/ticket-message-list.component';
import {
  TicketReplyComposerComponent,
  TicketReplyComposerLabels,
} from './ui/reply-composer/ticket-reply-composer.component';

@Component({
  selector: 'app-ticket-conversation-modal',
  standalone: true,
  imports: [
    CommonModule,
    ModalComponent,
    TicketAttachmentPreviewComponent,
    TicketMessageListComponent,
    TicketReplyComposerComponent,
  ],
  templateUrl: './ticket-conversation-modal.component.html',
  styleUrl: './ticket-conversation-modal.component.scss',
  providers: [TicketConversationStore],
})
export class TicketConversationModalComponent implements OnChanges, OnDestroy {
  readonly isOpen = input(false);
  readonly ticketNumber = input<string | null>(null);
  readonly closed = output<void>();
  readonly changed = output<void>();

  @ViewChild(TicketMessageListComponent)
  private messageList?: TicketMessageListComponent;
  @ViewChild(TicketReplyComposerComponent)
  private replyComposer?: TicketReplyComposerComponent;
  @ViewChild(TicketAttachmentPreviewComponent)
  private attachmentViewer?: TicketAttachmentPreviewComponent;

  readonly dictionary = inject(DictionaryService);
  readonly modalOpened = signal(false);
  readonly attachmentPreview = signal<TicketAttachmentPreview | null>(null);
  readonly ticketStatus = TicketStatus;
  readonly ticketSenderRole = TicketSenderRole;

  private readonly conversation = inject(TicketConversationStore);
  private readonly ticketApi = inject(TicketApiService);
  private readonly snackbar = inject(SnackbarService);
  private readonly auth = inject(AuthenticationService);

  readonly ticket = this.conversation.ticket;
  readonly messages = this.conversation.messages;
  readonly loading = this.conversation.loading;
  readonly sending = this.conversation.sending;
  readonly pollError = this.conversation.pollError;
  readonly resolveMediaUrl = (path: string): string =>
    this.ticketApi.resolveMediaUrl(path);
  readonly currentSenderId = signal<string | null>(null);
  readonly currentSenderName = signal('');
  readonly translateSender = (role: TicketSenderRole): string =>
    this.dictionary.get(role);

  private readonly scrolling = new TicketConversationScrollController(() =>
    this.messageList?.scrollToBottom(),
  );
  private readonly keepLatestMessageVisible = effect(() => {
    this.messages();
    if (this.modalOpened()) this.scrolling.messagesChanged();
  });

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['isOpen'] && !changes['ticketNumber']) return;
    this.modalOpened.set(this.isOpen());
    const ticketNumber = this.ticketNumber();
    if (this.isOpen() && ticketNumber) {
      void this.openConversation(ticketNumber);
    } else {
      this.scrolling.reset();
      this.conversation.close();
    }
  }

  composerLabels(): TicketReplyComposerLabels {
    return {
      placeholder: this.dictionary.get('WriteReply'),
      attachFiles: this.dictionary.get('AttachFiles'),
      attachmentLimits: this.dictionary.get('ReplyAttachmentLimits'),
      send: this.dictionary.get('Send'),
      sending: this.dictionary.get('Sending'),
      previewAttachment: this.dictionary.get('PreviewAttachment'),
      removeFile: this.dictionary.get('RemoveFile'),
      download: this.dictionary.get('Download'),
      closePreview: this.dictionary.get('ClosePreview'),
      loadingPreview: this.dictionary.get('LoadingAttachmentPreview'),
      spreadsheetSheet: this.dictionary.get('SpreadsheetSheet'),
      spreadsheetEmptySheet: this.dictionary.get('SpreadsheetEmptySheet'),
      spreadsheetPreviewTruncated: this.dictionary.get(
        'SpreadsheetPreviewTruncated',
      ),
    };
  }

  fileName(path: string): string {
    return decodeURIComponent(
      path.split('?')[0].split('/').pop() || this.dictionary.get('Attachment'),
    );
  }

  previewImage(path: string, event: Event): void {
    event.preventDefault();
    this.openImageAttachment({ path, trigger: this.eventElement(event) });
  }

  openImageAttachment(request: TicketAttachmentOpenEvent): void {
    const originalUrl = this.resolveMediaUrl(request.path);
    const preview = {
      kind: 'image',
      name: this.fileName(request.path),
      originalUrl,
      imageUrl: originalUrl,
    } satisfies TicketAttachmentPreview;
    this.attachmentPreview.set(preview);
    this.attachmentViewer?.openImage(
      preview.name,
      preview.originalUrl,
      request.trigger,
    );
  }

  async previewDocument(path: string, event: Event): Promise<void> {
    await this.openDocumentAttachment({
      path,
      trigger: this.eventElement(event),
    });
  }

  async openDocumentAttachment(
    request: TicketAttachmentOpenEvent,
  ): Promise<void> {
    const originalUrl = this.resolveMediaUrl(request.path);
    if (ticketAttachmentKind(request.path) === 'spreadsheet') {
      const preview = {
        kind: 'spreadsheet',
        name: this.fileName(request.path),
        originalUrl,
      } satisfies TicketAttachmentPreview;
      this.attachmentPreview.set(preview);
      await this.attachmentViewer?.openSpreadsheetUrl(
        preview.name,
        preview.originalUrl,
        request.trigger,
      );
      return;
    }
    const preview = {
      kind: 'pdf',
      name: this.fileName(request.path),
      originalUrl,
    } satisfies TicketAttachmentPreview;
    this.attachmentPreview.set(preview);
    await this.attachmentViewer?.openPdf(
      preview.name,
      preview.originalUrl,
      request.trigger,
    );
  }

  closeAttachmentPreview(): void {
    this.attachmentViewer?.close();
    this.attachmentPreview.set(null);
  }

  attachmentPreviewClosed(): void {
    this.attachmentPreview.set(null);
  }

  attachmentPreviewFailed(): void {
    this.attachmentPreview.set(null);
    this.snackbar.showError(this.dictionary.get('AttachmentPreviewFailed'));
  }

  setStickToBottom(value: boolean): void {
    this.scrolling.positionChanged(value);
  }

  contentSizeChanged(): void {
    this.scrolling.contentSizeChanged();
  }

  showFileSelectionError(error: TicketFileSelectionError): void {
    this.snackbar.showError(this.fileSelectionError(error));
  }

  async send(payload: TicketReplyPayload): Promise<void> {
    try {
      const message = await this.conversation.reply(payload);
      if (!message) return;
      this.replyComposer?.reset();
      this.scrolling.forceToBottom();
      this.replyComposer?.focus();
      this.changed.emit();
    } catch (error: unknown) {
      this.snackbar.showError(
        ticketApiErrorMessage(error, 'TicketReplyFailed'),
      );
    }
  }

  handleClose(): void {
    this.modalOpened.set(false);
    this.resetState();
    this.closed.emit();
  }

  ngOnDestroy(): void {
    this.resetState();
  }

  @HostListener('document:keydown.escape', ['$event'])
  onEscapeKey(event: Event): void {
    if (this.attachmentPreview()) {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.closeAttachmentPreview();
      return;
    }
    if (!this.modalOpened()) return;
    event.preventDefault();
    event.stopPropagation();
    this.handleClose();
  }

  private async openConversation(ticketNumber: string): Promise<void> {
    this.resetState();
    this.modalOpened.set(true);
    try {
      const currentUser = await this.auth.getUserData();
      if (!this.isOpen() || this.ticketNumber() !== ticketNumber) return;
      this.currentSenderId.set(currentUser?.id ?? null);
      this.currentSenderName.set(currentUser?.name ?? '');
      await this.conversation.open(
        ticketNumber,
        TicketSenderRole.USER,
        this.currentSenderId(),
      );
      this.scrolling.forceToBottom();
    } catch (error: unknown) {
      this.snackbar.showError(ticketApiErrorMessage(error, 'TicketLoadFailed'));
    }
  }

  private resetState(): void {
    this.scrolling.reset();
    this.conversation.close();
    this.closeAttachmentPreview();
    this.replyComposer?.reset();
    this.currentSenderId.set(null);
    this.currentSenderName.set('');
  }

  private eventElement(event: Event): HTMLElement | undefined {
    return event.currentTarget instanceof HTMLElement
      ? event.currentTarget
      : undefined;
  }

  private fileSelectionError(error: TicketFileSelectionError): string {
    const keys: Record<TicketFileSelectionError, string> = {
      unsupported: 'TicketUnsupportedFile',
      size: 'TicketFileTooLarge',
      attachments: 'TicketAttachmentLimit',
      audio: 'TicketAudioLimit',
      video: 'TicketVideoLimit',
    };
    return keys[error];
  }
}
